import React, { createContext, useState, useContext, useEffect } from 'react';
import frameworkService from '../services/frameworkService';
import { useAuth } from './AuthContext';

// Create the Framework context
const FrameworkContext = createContext();

/**
 * Provider component for the Framework context.
 * 
 * @param {object} props - Component props
 * @returns {JSX.Element} Context provider
 */
export const FrameworkProvider = ({ children }) => {
  const [frameworks, setFrameworks] = useState([]);
  const [activeFramework, setActiveFramework] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  // Fetch frameworks when the provider mounts and the user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchFrameworks();
    }
  }, [isAuthenticated]);

  /**
   * Fetch all frameworks from the API.
   */
  const fetchFrameworks = async () => {
    try {
      setLoading(true);
      const data = await frameworkService.getAllFrameworks();
      setFrameworks(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching frameworks:', err);
      setError('Failed to load frameworks. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Set the active framework and fetch its details.
   * Also dispatches an event to notify other components about the framework change.
   * 
   * @param {string} frameworkId - The ID of the framework to set as active
   */
  const selectFramework = async (frameworkId) => {
    try {
      setLoading(true);
      const data = await frameworkService.getFramework(frameworkId);
      setActiveFramework(data);
      setError(null);
      
      // Dispatch a custom event to signal framework change
      // This will be used to reset chat context when switching frameworks
      window.dispatchEvent(new CustomEvent('framework-changed', {
        detail: {
          frameworkId: frameworkId,
          frameworkTitle: data.title
        }
      }));
    } catch (err) {
      console.error('Error selecting framework:', err);
      setError('Failed to load framework details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle the completion status of a step.
   * 
   * @param {string} stepId - The ID of the step to toggle
   * @param {boolean} isCompleted - The new completion status
   */
  const toggleStepCompletion = async (stepId, isCompleted) => {
    try {
      // Optimistically update the UI
      if (activeFramework) {
        const updatedSteps = activeFramework.steps.map(step => {
          if (step.id === stepId) {
            return { ...step, completed: isCompleted };
          }
          return step;
        });
        
        setActiveFramework({
          ...activeFramework,
          steps: updatedSteps
        });
      }
      
      // Update in the backend
      await frameworkService.updateStepCompletion(stepId, isCompleted);
    } catch (err) {
      console.error('Error updating step completion:', err);
      // Revert the optimistic update on error
      if (activeFramework) {
        const originalCompletion = !isCompleted;
        const revertedSteps = activeFramework.steps.map(step => {
          if (step.id === stepId) {
            return { ...step, completed: originalCompletion };
          }
          return step;
        });
        
        setActiveFramework({
          ...activeFramework,
          steps: revertedSteps
        });
      }
      setError('Failed to update step completion. Please try again later.');
    }
  };

  // Clear the active framework
  const clearActiveFramework = () => {
    setActiveFramework(null);
  };

  // Refresh the active framework data
  const refreshActiveFramework = async () => {
    if (activeFramework) {
      await selectFramework(activeFramework.id);
    }
  };

  // The value provided to consumers of this context
  const value = {
    frameworks,
    activeFramework,
    loading,
    error,
    selectFramework,
    toggleStepCompletion,
    clearActiveFramework,
    refreshActiveFramework
  };

  return (
    <FrameworkContext.Provider value={value}>
      {children}
    </FrameworkContext.Provider>
  );
};

/**
 * Custom hook to use the Framework context.
 * 
 * @returns {object} The Framework context value
 */
export const useFramework = () => {
  const context = useContext(FrameworkContext);
  if (!context) {
    throw new Error('useFramework must be used within a FrameworkProvider');
  }
  return context;
};